import Vue from 'vue';
import Router from 'vue-router';
import Model from './components/wizard-steps/Model';
import LoadConfiguration from './components/behavior/LoadConfiguration';
import PhysicianTable from './components/wizard-steps/PhysicianTable';
import Instruments from './components/wizard-steps/Instruments';
import SpittoonBlock from './components/wizard-steps/SpittoonBlock';
import Lamp from './components/wizard-steps/Lamp';
import Chair from './components/wizard-steps/Chair';
import Color from './components/wizard-steps/Color';
import FootControl from './components/wizard-steps/FootControl';
import Addons from './components/wizard-steps/Addons';
import AssistantTable from './components/wizard-steps/AssistantTable';
import PartnerDashboard from './components/partner/PartnerDashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminConfigurations from './components/admin/AdminConfigurations';
import AdminPricelist from './components/admin/AdminPricelist';
import store from './store';
import {api} from './core/services/api';

Vue.use(Router);

const routerInstance = new Router({
	routes: [
		{
			path: '/',
			component: Model,
			meta: {
				next: 'physician-table',
			},
		},
		{
			path: '/load-configuration/:unitConfigurationId',
			name: 'load-configuration',
			component: LoadConfiguration,
			meta: {
				isPartnerPage: true,
				isViewableByAdmin: true,
			},
		},
		{
			path: '/model/:name?',
			name: 'model',
			component: Model,
			meta: {
				next: 'physician-table',
			},
		},
		{
			path: '/physician-table',
			name: 'physician-table',
			component: PhysicianTable,
			meta: {
				prev: 'model',
				next: 'instruments',
			},
		},
		{
			path: '/instruments',
			name: 'instruments',
			component: Instruments,
			meta: {
				prev: 'physician-table',
				next: 'spittoon-block',
			},
		},
		{
			path: '/spittoon-block',
			name: 'spittoon-block',
			component: SpittoonBlock,
			meta: {
				prev: 'instruments',
				next: 'assistant-table',
			},
		},
		{
			path: '/lamp',
			name: 'lamp',
			component: Lamp,
			meta: {
				prev: 'assistant-table',
				next: 'chair',
			},
		},
		{
			path: '/chair',
			name: 'chair',
			component: Chair,
			meta: {
				prev: 'lamp',
				next: 'color',
			},
		},
		{
			path: '/color',
			name: 'color',
			component: Color,
			meta: {
				prev: 'chair',
				next: 'foot-control',
			},
		},
		{
			path: '/foot-control',
			name: 'foot-control',
			component: FootControl,
			meta: {
				prev: 'color',
				next: 'additional-equipment',
			},
		},
		{
			path: '/additional-equipment',
			name: 'additional-equipment',
			component: Addons,
			meta: {
				prev: 'foot-control',
				submittable: true,
			},
		},
		{
			path: '/assistant-table',
			name: 'assistant-table',
			component: AssistantTable,
			meta: {
				prev: 'spittoon-block',
				next: 'lamp',
			},
		},
		{
			path: '/partner',
			name: 'partner',
			component: PartnerDashboard,
			meta: {
				isPartnerPage: true,
			},
		},
		{
			path: '/admin',
			name: 'admin',
			component: AdminDashboard,
			meta: {
				isAdminPage: true,
			},
		},
		{
			path: '/admin/configurations',
			name: 'admin-configurations',
			component: AdminConfigurations,
			meta: {
				isAdminPage: true,
			},
		},
		{
			path: '/admin/pricelist',
			name: 'admin-pricelist',
			component: AdminPricelist,
			meta: {
				isAdminPage: true,
			},
		},
	],
});

routerInstance.beforeEach(async (to, from, next) => {
	if (to.meta.isAdminPage) {
		try {
			await api.get('/adminMe');
		} catch (e) {
			if (e.message !== 'Request aborted') {
				next('/');
				return;
			}
		}
		next();
		return;
	}

	if (!to.meta.isPartnerPage) {
		if (to.query.role === 'admin' && to.query.unitConfigurationId) {
			try {
				await api.get('/adminMe');
				next();
			} catch (e) {
				if (e.message !== 'Request aborted') {
					next({name: to.name, params: to.params, query: {unitConfigurationId: undefined}});
				}
			}
		} else if (!store.state.user && to.query.unitConfigurationId) {
			try {
				const user = (await api.get('/users/me')).data;
				store.commit('setUser', user);
				next({
					name: 'load-configuration',
					params: {unitConfigurationId: to.query.unitConfigurationId},
				});
			} catch (e) {
				if (e.message !== 'Request aborted') {
					next({name: to.name, params: to.params, query: {unitConfigurationId: undefined}});
				}
			}
		} else if (!store.state.user && from.name === null) {
			try {
				const user = (await api.get('/users/me')).data;
				store.commit('setUser', user);
				next();
			} catch (e) {
				next();
			}
		} else if (
			from.query.unitConfigurationId &&
			!Object.keys(to.query).includes('unitConfigurationId')
		) {
			next({name: to.name, params: to.params, query: from.query});
		} else {
			next();
		}
		return;
	}

	if (!store.state.user) {
		try {
			if (to.meta.isViewableByAdmin && to.query.role === 'admin') {
				await api.get('/adminMe');
			} else {
				const user = (await api.get('/users/me')).data;
				store.commit('setUser', user);
			}
		} catch (e) {
			if (e.message !== 'Request aborted') {
				next('/model');
				return;
			}
		}
	}

	next();
});

routerInstance.beforeEach(async (to, from, next) => {
	if (to.meta && to.meta.prev) {
		store.commit('setConfiguratorDirty', true);
	}
	next();
});

export default routerInstance;
